
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFundsDistribution } from '../../services/walletService';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, Spinner, Table, Alert, Row, Col } from 'react-bootstrap';
import { FaCoins, FaPercentage, FaExternalLinkAlt } from 'react-icons/fa';
import styles from './FundsDistribution.module.css';

const COLORS_BY_USAGE = {
  'Game Fund': '#FFD700',
  'Burn Address': '#FF4500',
  'Rewards': '#1E90FF',
  'Team Donation': '#32CD32',
};

const COLOR_NON_BURN_AGG = '#6c757d';

const formatMenel = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatPercent = (n) => `${Number(n || 0).toFixed(2)}%`;
const clampPct = (n) => Math.max(0, Math.min(100, Number(n || 0)));

const piePercentLabel = ({ value }) => `${formatPercent(value)}`;

const FundsDistribution = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getFundsDistribution();
        setData(res);
      } catch (e) {
        console.error(e);
        setErr('load_error'); // se muestra texto traducido
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const rows = useMemo(() => {
    const raw = Object.values(data?.addresses || {}).map((d) => ({
      usage: d.usage,
      address: d.address,
      balance_menel: Number(d.balance_menel || 0),
      balance_percentage: Number(d.balance_percentage || 0),
      received_menel: Number(d.received_menel || 0),
      received_percentage: Number(d.received_percentage || 0),
    }));
    return raw.sort((a, b) => b.balance_menel - a.balance_menel);
  }, [data]);

  const burnRows = useMemo(() => rows.filter((r) => r.usage === 'Burn Address'), [rows]);
  const nonBurnRows = useMemo(() => rows.filter((r) => r.usage !== 'Burn Address'), [rows]);

  // Totales No Burn
  const nonBurnBalanceTotal = useMemo(
    () => nonBurnRows.reduce((acc, r) => acc + r.balance_menel, 0),
    [nonBurnRows]
  );
  const nonBurnReceivedTotal = useMemo(
    () => nonBurnRows.reduce((acc, r) => acc + r.received_menel, 0),
    [nonBurnRows]
  );

  // Totales Burn
  const burnBalanceTotal = useMemo(
    () => burnRows.reduce((acc, r) => acc + r.balance_menel, 0),
    [burnRows]
  );
  const burnReceivedTotal = useMemo(
    () => burnRows.reduce((acc, r) => acc + r.received_menel, 0),
    [burnRows]
  );

  // Filas No Burn con % recalculado EXCLUYENDO Burn (para gráficos y barras)
  const nonBurnRowsWithRecalc = useMemo(() => {
    return nonBurnRows.map((r) => ({
      ...r,
      balance_percentage_excl_burn:
        nonBurnBalanceTotal > 0 ? (r.balance_menel * 100) / nonBurnBalanceTotal : 0,
      received_percentage_excl_burn:
        nonBurnReceivedTotal > 0 ? (r.received_menel * 100) / nonBurnReceivedTotal : 0,
    }));
  }, [nonBurnRows, nonBurnBalanceTotal, nonBurnReceivedTotal]);

  // Comparativa BALANCE: No Burn vs Burn en %
  const balanceBurnVsNonBurnPct = useMemo(() => {
    const total = nonBurnBalanceTotal + burnBalanceTotal;
    const nbPct = total > 0 ? (nonBurnBalanceTotal * 100) / total : 0;
    const burnPct = total > 0 ? (burnBalanceTotal * 100) / total : 0;
    return [
      { name: t('funds.section_no_burn'), pct: nbPct, color: COLOR_NON_BURN_AGG },
      { name: t('funds.section_burn'), pct: burnPct, color: COLORS_BY_USAGE['Burn Address'] },
    ];
  }, [nonBurnBalanceTotal, burnBalanceTotal, t]);

  if (loading) {
    return (
      <div className={`${styles.fullscreen} ${styles.bg}`}>
        <div className={styles.loaderCard}>
          <Spinner animation="border" role="status" />
          <p className={styles.loadingText}>{t('funds.loading')}</p>
        </div>
      </div>
    );
  }

  if (err) return <Alert variant="danger" className="mt-3">{t('funds.error_load')}</Alert>;
  if (!data) return <Alert variant="warning" className="mt-3">{t('funds.no_data')}</Alert>;

  const renderLegend = (props) => {
    const { payload } = props || {};
    return (
      <ul className={styles.legendList}>
        {payload?.map((entry, idx) => (
          <li key={idx} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ backgroundColor: entry.color }} />
            {entry.value}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={`${styles.bg} container-fluid py-4`}>
      <div className={`container ${styles.container}`}>
        <h2 className={styles.title}>
          <FaCoins className={styles.titleIcon} /> {t('funds.title')}
        </h2>

        {/* Tres gráficas en la misma fila (todas en %) */}
        <Row className="g-4">
          {/* 1) Balance Distribution (No Burn) – % */}
          <Col md={4}>
            <Card className={styles.card}>
              <Card.Body>
                <Card.Title className={styles.cardTitle}>{t('funds.balance_no_burn')}</Card.Title>
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={nonBurnRowsWithRecalc}
                      dataKey="balance_percentage_excl_burn"
                      nameKey="usage"
                      outerRadius={120}
                      label={piePercentLabel}
                      className={styles.pieHover}
                    >
                      {nonBurnRowsWithRecalc.map((r) => (
                        <Cell key={`nb-bal-${r.address}`} fill={COLORS_BY_USAGE[r.usage] || '#8884d8'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatPercent(v)} />
                    <Legend content={renderLegend} />
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.helperNote}>{t('funds.helper_no_burn')}</div>
              </Card.Body>
            </Card>
          </Col>

          {/* 2) Received Distribution (No Burn) – % */}
          <Col md={4}>
            <Card className={styles.card}>
              <Card.Body>
                <Card.Title className={styles.cardTitle}>{t('funds.received_no_burn')}</Card.Title>
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={nonBurnRowsWithRecalc}
                      dataKey="received_percentage_excl_burn"
                      nameKey="usage"
                      outerRadius={120}
                      label={piePercentLabel}
                      className={styles.pieHover}
                    >
                      {nonBurnRowsWithRecalc.map((r) => (
                        <Cell key={`nb-rec-${r.address}`} fill={COLORS_BY_USAGE[r.usage] || '#8884d8'} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatPercent(v)} />
                    <Legend content={renderLegend} />
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.helperNote}>{t('funds.helper_no_burn')}</div>
              </Card.Body>
            </Card>
          </Col>

          {/* 3) Balance – No Burn vs Burn – % */}
          <Col md={4}>
            <Card className={styles.card}>
              <Card.Body>
                <Card.Title className={styles.cardTitle}>{t('funds.balance_nb_vs_burn')}</Card.Title>
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={balanceBurnVsNonBurnPct}
                      dataKey="pct"
                      nameKey="name"
                      outerRadius={120}
                      label={piePercentLabel}
                      className={styles.pieHover}
                    >
                      {balanceBurnVsNonBurnPct.map((d, i) => (
                        <Cell key={`agg-bal-${i}`} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatPercent(v)} />
                    <Legend content={renderLegend} />
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.helperNote}>{t('funds.helper_nb_vs_burn')}</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla: No Burn y Burn */}
        <Card className={`${styles.card} mt-4`}>
          <Card.Body>
            <Card.Title className={styles.cardTitle}>
              <FaPercentage className="me-2" /> {t('funds.table_title')}
            </Card.Title>

            {/* No Burn */}
            <h5 className={styles.sectionHeader}>{t('funds.section_no_burn')}</h5>
            <div className={styles.tableWrap}>
              <Table hover responsive className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th>{t('funds.col_address')}</th>
                    <th>{t('funds.col_usage')}</th>
                    <th className={styles.numCol}>{t('funds.col_balance')}</th>
                    <th className={styles.numCol}>{t('funds.col_balance_pct_nb')}</th>
                    <th className={styles.numCol}>{t('funds.col_received')}</th>
                    <th className={styles.numCol}>{t('funds.col_received_pct_nb')}</th>
                  </tr>
                </thead>
                <tbody>
                  {nonBurnRowsWithRecalc.length === 0 && (
                    <tr><td colSpan={6} className={styles.emptyCell}>{t('funds.no_addresses')}</td></tr>
                  )}
                  {nonBurnRowsWithRecalc.map((r) => (
                    <tr key={`nonburn-${r.address}`} className={styles.row}>
                      <td className={styles.addressCell}>
                        <a
                          href={`https://explorer.menelik.cc/address/${r.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.addressLink}
                          title={r.address}
                        >
                          <span
                            className={styles.addressBadge}
                            style={{ backgroundColor: COLORS_BY_USAGE[r.usage] || '#8884d8' }}
                          />
                          <span className={styles.addressText}>{r.address}</span>
                          <FaExternalLinkAlt size={12} className={styles.externalIcon} />
                        </a>
                      </td>

                      <td className={styles.usageCell}>
                        <span
                          className={styles.usagePill}
                          style={{
                            backgroundColor: `${(COLORS_BY_USAGE[r.usage] || '#8884d8')}22`,
                            borderColor: COLORS_BY_USAGE[r.usage] || '#8884d8'
                          }}
                        >
                          {r.usage}
                        </span>
                      </td>

                      <td className={styles.numCol}>{formatMenel(r.balance_menel)}</td>

                      {/* Balance % (excl. Burn) con barra */}
                      <td className={styles.progressCell}>
                        <div className={styles.progressWrapper} title={formatPercent(r.balance_percentage_excl_burn)}>
                          <div className={styles.progressTrack} />
                          <div
                            className={styles.progressFill}
                            style={{
                              width: `${clampPct(r.balance_percentage_excl_burn)}%`,
                              background: `linear-gradient(90deg, ${(COLORS_BY_USAGE[r.usage] || '#8884d8')} 0%, ${(COLORS_BY_USAGE[r.usage] || '#8884d8')}cc 100%)`,
                            }}
                          />
                          <span className={styles.progressLabel}>{formatPercent(r.balance_percentage_excl_burn)}</span>
                        </div>
                      </td>

                      <td className={styles.numCol}>{formatMenel(r.received_menel)}</td>

                      {/* Received % (excl. Burn) con barra */}
                      <td className={styles.progressCell}>
                        <div className={styles.progressWrapper} title={formatPercent(r.received_percentage_excl_burn)}>
                          <div className={styles.progressTrack} />
                          <div
                            className={styles.progressFill}
                            style={{
                              width: `${clampPct(r.received_percentage_excl_burn)}%`,
                              background: `linear-gradient(90deg, ${(COLORS_BY_USAGE[r.usage] || '#8884d8')} 0%, ${(COLORS_BY_USAGE[r.usage] || '#8884d8')}cc 100%)`,
                            }}
                          />
                          <span className={styles.progressLabel}>{formatPercent(r.received_percentage_excl_burn)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Burn */}
            <h5 className={`${styles.sectionHeader} mt-4`}>{t('funds.section_burn')}</h5>
            <div className={styles.tableWrap}>
              <Table hover responsive className={styles.table}>
                <thead className={styles.thead}>
                  <tr>
                    <th>{t('funds.col_address')}</th>
                    <th>{t('funds.col_usage')}</th>
                    <th className={styles.numCol}>{t('funds.col_balance')}</th>
                    <th className={styles.numCol}>{t('funds.col_received')}</th>
                  </tr>
                </thead>
                <tbody>
                  {burnRows.length === 0 && (
                    <tr><td colSpan={4} className={styles.emptyCell}>{t('funds.no_burn_addresses')}</td></tr>
                  )}
                  {burnRows.map((r) => (
                    <tr key={`burn-${r.address}`} className={styles.row}>
                      <td className={styles.addressCell}>
                        <a
                          href={`https://explorer.menelik.cc/address/${r.address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.addressLink}
                          title={r.address}
                        >
                          <span
                            className={styles.addressBadge}
                            style={{ backgroundColor: COLORS_BY_USAGE['Burn Address'] }}
                          />
                          <span className={styles.addressText}>{r.address}</span>
                          <FaExternalLinkAlt size={12} className={styles.externalIcon} />
                        </a>
                      </td>
                      <td className={styles.usageCell}>
                        <span
                          className={styles.usagePill}
                          style={{
                            backgroundColor: `${(COLORS_BY_USAGE['Burn Address'])}22`,
                            borderColor: COLORS_BY_USAGE['Burn Address']
                          }}
                        >
                          {r.usage}
                        </span>
                      </td>
                      <td className={styles.numCol}>{formatMenel(r.balance_menel)}</td>
                      <td className={styles.numCol}>{formatMenel(r.received_menel)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className={styles.totals}>
              <div><strong>{t('funds.total_balance_nb')}</strong> {formatMenel(nonBurnBalanceTotal)} MENEL</div>
              <div><strong>{t('funds.total_balance_burn')}</strong> {formatMenel(burnBalanceTotal)} MENEL</div>
              <div><strong>{t('funds.total_received_nb')}</strong> {formatMenel(nonBurnReceivedTotal)} MENEL</div>
              <div><strong>{t('funds.total_received_burn')}</strong> {formatMenel(burnReceivedTotal)} MENEL</div>
              <div className={styles.updatedAt}>{t('funds.updated_at')} {new Date(data.updated_at).toLocaleString()}</div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default FundsDistribution;
